/** Values shared by the API, the public site and the admin CMS. */

export const AUDIENCES = ['men', 'women', 'unisex'] as const;
export type Audience = (typeof AUDIENCES)[number];

export const PRICING_TYPES = ['fixed', 'starting-from', 'range', 'variants', 'text'] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

export const MEDIA_TYPES = ['image', 'video', 'svg'] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

export const MEDIA_CATEGORIES = [
  'branding', 'home', 'services', 'gallery', 'transformations',
  'packages', 'branches', 'videos', 'offers', 'pages', 'uploads',
] as const;
export type MediaCategory = (typeof MEDIA_CATEGORIES)[number];

export const USER_ROLES = ['SUPER_ADMIN', 'CONTENT_ADMIN', 'EDITOR'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Every permission the admin API checks. Roles are expanded to these at request time
 * so a route never has to know about role names.
 */
export const PERMISSIONS = [
  'settings:read', 'settings:write',
  'navigation:read', 'navigation:write',
  'pages:read', 'pages:write', 'pages:publish',
  'services:read', 'services:write', 'services:delete',
  'categories:read', 'categories:write', 'categories:delete',
  'offers:read', 'offers:write', 'offers:delete',
  'packages:read', 'packages:write', 'packages:delete',
  'gallery:read', 'gallery:write', 'gallery:delete',
  'media:read', 'media:write', 'media:delete',
  'transformations:read', 'transformations:write', 'transformations:delete',
  'testimonials:read', 'testimonials:write', 'testimonials:delete',
  'branches:read', 'branches:write', 'branches:delete',
  'homepage:read', 'homepage:write',
  'enquiries:read', 'enquiries:write',
  'seo:read', 'seo:write',
  'redirects:read', 'redirects:write',
  'users:read', 'users:write',
  'audit:read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const CONTENT_PERMISSIONS: Permission[] = PERMISSIONS.filter(
  (p) => !p.startsWith('users:') && !p.startsWith('audit:'),
) as Permission[];

const EDITOR_PERMISSIONS: Permission[] = CONTENT_PERMISSIONS.filter(
  (p) => !p.startsWith('settings:write') && !p.startsWith('redirects:write') && !p.startsWith('branches:delete'),
) as Permission[];

export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  SUPER_ADMIN: PERMISSIONS,
  CONTENT_ADMIN: CONTENT_PERMISSIONS,
  EDITOR: EDITOR_PERMISSIONS,
};

export function roleHas(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export const SECTION_TYPES = [
  'hero', 'richText', 'imageGrid', 'gallery', 'services', 'offers',
  'packages', 'testimonials', 'transformations', 'featuredWork',
  'branches', 'cta', 'customSeoContent', 'ticker', 'stats',
  'curatedServices', 'servicePills', 'imageCarousel', 'serviceMenu',
  'contactInfo',
] as const;
export type SectionType = (typeof SECTION_TYPES)[number];

export const PAGE_TYPES = [
  'home', 'service-menu', 'gallery', 'contact', 'legal',
  'offers', 'campaign', 'locations', 'seo-landing', 'custom',
] as const;
export type PageType = (typeof PAGE_TYPES)[number];

export const ENQUIRY_STATUSES = ['new', 'contacted', 'booked', 'closed', 'spam'] as const;
export type EnquiryStatus = (typeof ENQUIRY_STATUSES)[number];

/**
 * Where contact-form enquiries are emailed when Site settings' "Send enquiries
 * to" is blank. Hard-coded so a fresh install - and production, whose settings
 * predate the field - notifies the salon without anyone configuring it.
 */
export const DEFAULT_CONTACT_EMAIL_TO = 'charlieandguys07@gmail.com';

export const MENU_LOCATIONS = ['header', 'footer', 'mobile'] as const;
export type MenuLocation = (typeof MENU_LOCATIONS)[number];

export const CTA_TYPES = ['whatsapp', 'phone', 'link', 'none'] as const;
export type CtaType = (typeof CTA_TYPES)[number];

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

/**
 * WhatsApp message templates. The live site hard-codes each message into its
 * button; here they are named templates with {placeholders}, editable in admin.
 */
export const WHATSAPP_TEMPLATE_KEYS = [
  'booking', 'branchBooking', 'offerBooking', 'comboEnquiry',
  'serviceBooking', 'transformationEnquiry', 'packageEnquiry', 'generalEnquiry',
] as const;
export type WhatsAppTemplateKey = (typeof WHATSAPP_TEMPLATE_KEYS)[number];

/** Defaults reproduce the exact strings observed on the live site. */
export const DEFAULT_WHATSAPP_TEMPLATES: Record<WhatsAppTemplateKey, string> = {
  booking: "Hi Charlie & Guys! I'd like to book an appointment.",
  branchBooking: "Hi Charlie & Guys! I'd like to book an appointment at your {branch} salon.",
  offerBooking: "Hi, I'd like to book {name} - ₹{price}",
  comboEnquiry: "Hi, I'd like to know more about your combo offers",
  serviceBooking: "Hi Charlie & Guys! I'd like to book {name}.",
  transformationEnquiry:
    'Hi Charlie & Guys! I loved the {name} on your website. I would like to book an appointment.',
  packageEnquiry: "Hi Charlie & Guys! I'd like to know more about the {name} prepaid package.",
  generalEnquiry: 'Hi Charlie & Guys! I have a question.',
};

export const IMAGE_DERIVATIVES = [
  { name: 'thumbnail', width: 400 },
  { name: 'medium', width: 800 },
  { name: 'large', width: 1600 },
] as const;

export const ALLOWED_IMAGE_MIME = [
  'image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif', 'image/svg+xml',
] as const;

export const ALLOWED_VIDEO_MIME = ['video/mp4', 'video/webm', 'video/quicktime'] as const;

export const API_ERROR_CODES = {
  VALIDATION: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNSUPPORTED_MEDIA: 'UNSUPPORTED_MEDIA_TYPE',
  INTERNAL: 'INTERNAL_ERROR',
} as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;
